import React, { useCallback } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
} from '@mui/material';
import { Description as TemplateIcon } from '@mui/icons-material';
import { adjustOpacity } from '../../../../theme/utils';

/**
 * TemplateInfoCard Component
 *
 * Displays information about the selected template.
 *
 * @param {Object} props
 * @param {Object} props.selectedTemplate - Selected template object
 * @param {Array} props.templateVersions - Array of available template versions
 * @param {boolean} props.templateLoading - Loading state for template versions
 * @param {boolean} props.longLoading - Whether loading is taking longer than expected
 */
function TemplateInfoCard({
  selectedTemplate,
  templateVersions,
  templateLoading,
  longLoading,
}) {
  const theme = useTheme();

  // Format model configuration for tooltip
  const formatModelConfig = useCallback(() => {
    const rawConfig =
      selectedTemplate?.llm_config || selectedTemplate?.model_config;
    if (!rawConfig) {
      return { provider: 'N/A', model: 'N/A' };
    }

    let parsed = rawConfig;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { provider: 'N/A', model: 'N/A' };
      }
    }

    return {
      provider: parsed.provider || 'N/A',
      model: parsed.model_name || parsed.model || 'N/A',
      temperature: parsed.temperature,
      max_tokens: parsed.max_tokens,
      top_p: parsed.top_p,
      frequency_penalty: parsed.frequency_penalty,
      presence_penalty: parsed.presence_penalty,
    };
  }, [selectedTemplate]);

  // Create tooltip content for version chip
  const createVersionTooltip = useCallback(() => {
    const modelConfig = formatModelConfig();

    // Apple-style colors from theme
    const isDark = theme.palette.mode === 'dark';
    const bgColor = isDark
      ? theme.palette.grey[900]
      : theme.palette.background.paper;
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const primaryColor = theme.palette.primary.main;
    const warningColor = theme.palette.warning.main;

    return (
      <Box
        sx={{
          p: 2.5,
          maxWidth: 380,
          bgcolor: bgColor,
          color: textPrimary,
          borderRadius: 2,
          boxShadow: theme.shadows[4],
          border: 'none',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{
            color: primaryColor,
            fontSize: '0.95rem',
            mb: 1.5,
            letterSpacing: '0.01em',
          }}
        >
          Model Configuration
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="body2"
            display="block"
            sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
          >
            <Box
              component="span"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                mr: 1.5,
                minWidth: 90,
                display: 'inline-block',
              }}
            >
              Provider:
            </Box>
            <Box component="span" sx={{ color: textPrimary, fontWeight: 400 }}>
              {modelConfig.provider}
            </Box>
          </Typography>
          <Typography
            variant="body2"
            display="block"
            sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
          >
            <Box
              component="span"
              sx={{
                color: textSecondary,
                fontWeight: 500,
                mr: 1.5,
                minWidth: 90,
                display: 'inline-block',
              }}
            >
              Model:
            </Box>
            <Box component="span" sx={{ color: textPrimary, fontWeight: 400 }}>
              {modelConfig.model}
            </Box>
          </Typography>
          {modelConfig.temperature !== undefined && (
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Temperature:
              </Box>
              <Box
                component="span"
                sx={{ color: warningColor, fontWeight: 500 }}
              >
                {modelConfig.temperature}
              </Box>
            </Typography>
          )}
          {modelConfig.max_tokens !== undefined && (
            <Typography
              variant="body2"
              display="block"
              sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
            >
              <Box
                component="span"
                sx={{
                  color: textSecondary,
                  fontWeight: 500,
                  mr: 1.5,
                  minWidth: 90,
                  display: 'inline-block',
                }}
              >
                Max Tokens:
              </Box>
              <Box
                component="span"
                sx={{ color: warningColor, fontWeight: 500 }}
              >
                {modelConfig.max_tokens}
              </Box>
            </Typography>
          )}
        </Box>
      </Box>
    );
  }, [formatModelConfig, theme]);

  return (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <TemplateIcon
            sx={{ fontSize: 18, color: theme.palette.primary.main }}
          />
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              fontSize: '0.875rem',
              color: theme.palette.text.primary,
            }}
          >
            Selected Template
          </Typography>
        </Box>
        {selectedTemplate.description && (
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              lineHeight: 1.4,
              color: theme.palette.text.secondary,
              fontSize: '0.8125rem',
            }}
          >
            {selectedTemplate.description}
          </Typography>
        )}
        <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
          <Chip
            label={selectedTemplate.category || 'general'}
            size="small"
            sx={{
              height: 'auto',
              minHeight: 24,
              minWidth: 'fit-content',
              fontSize: '0.75rem',
              fontWeight: 500,
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              '& .MuiChip-label': {
                px: 2,
                py: 0.5,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: 'block',
              },
            }}
          />
          <Chip
            label={selectedTemplate.status?.toUpperCase() || 'ACTIVE'}
            size="small"
            sx={{
              height: 'auto',
              minHeight: 24,
              minWidth: 'fit-content',
              fontSize: '0.75rem',
              fontWeight: 600,
              backgroundColor: theme.palette.success.main,
              color: theme.palette.success.contrastText,
              '& .MuiChip-label': {
                px: 2,
                py: 0.5,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: 'block',
              },
            }}
          />
          <Tooltip
            title={createVersionTooltip()}
            arrow
            placement="right"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  padding: 0,
                  maxWidth: 'none',
                },
              },
              arrow: {
                sx: {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[900]
                      : theme.palette.background.paper,
                },
              },
            }}
          >
            <Chip
              label={`v${selectedTemplate.version || 1}`}
              size="small"
              variant="outlined"
              sx={{
                height: 'auto',
                minHeight: 24,
                minWidth: 'fit-content',
                fontSize: '0.75rem',
                fontWeight: 500,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiChip-label': {
                  px: 2,
                  py: 0.5,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  display: 'block',
                },
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  transform: 'scale(1.05)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                },
              }}
            />
          </Tooltip>
        </Box>

        {/* Available Versions */}
        {templateLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress
              size={16}
              sx={{ color: theme.palette.primary.main }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
              }}
            >
              Loading template versions...
            </Typography>
          </Box>
        ) : templateVersions.length > 0 ? (
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                mb: 0.75,
                color: theme.palette.text.primary,
              }}
            >
              Available Versions ({templateVersions.length})
            </Typography>
            <Box display="flex" gap={0.75} flexWrap="wrap">
              {templateVersions.map(version => {
                // Format model configuration for this version
                const formatVersionModelConfig = () => {
                  const rawConfig =
                    version?.llm_config || version?.model_config;
                  if (!rawConfig) {
                    return { provider: 'N/A', model: 'N/A' };
                  }

                  let parsed = rawConfig;
                  if (typeof parsed === 'string') {
                    try {
                      parsed = JSON.parse(parsed);
                    } catch {
                      return { provider: 'N/A', model: 'N/A' };
                    }
                  }

                  return {
                    provider: parsed.provider || 'N/A',
                    model: parsed.model_name || parsed.model || 'N/A',
                    temperature: parsed.temperature,
                    max_tokens: parsed.max_tokens,
                    top_p: parsed.top_p,
                    frequency_penalty: parsed.frequency_penalty,
                    presence_penalty: parsed.presence_penalty,
                  };
                };

                const versionModelConfig = formatVersionModelConfig();

                // Create tooltip for this version
                const createVersionTooltipContent = () => {
                  const isDark = theme.palette.mode === 'dark';
                  const bgColor = isDark
                    ? theme.palette.grey[900]
                    : theme.palette.background.paper;
                  const textPrimary = theme.palette.text.primary;
                  const textSecondary = theme.palette.text.secondary;
                  const primaryColor = theme.palette.primary.main;
                  const warningColor = theme.palette.warning.main;

                  return (
                    <Box
                      sx={{
                        p: 2.5,
                        maxWidth: 380,
                        bgcolor: bgColor,
                        color: textPrimary,
                        borderRadius: 2,
                        boxShadow: theme.shadows[4],
                        border: 'none',
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        gutterBottom
                        sx={{
                          color: primaryColor,
                          fontSize: '0.95rem',
                          mb: 1.5,
                          letterSpacing: '0.01em',
                        }}
                      >
                        Model Configuration
                      </Typography>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="body2"
                          display="block"
                          sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: textSecondary,
                              fontWeight: 500,
                              mr: 1.5,
                              minWidth: 90,
                              display: 'inline-block',
                            }}
                          >
                            Provider:
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: textPrimary, fontWeight: 400 }}
                          >
                            {versionModelConfig.provider}
                          </Box>
                        </Typography>
                        <Typography
                          variant="body2"
                          display="block"
                          sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.6 }}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: textSecondary,
                              fontWeight: 500,
                              mr: 1.5,
                              minWidth: 90,
                              display: 'inline-block',
                            }}
                          >
                            Model:
                          </Box>
                          <Box
                            component="span"
                            sx={{ color: textPrimary, fontWeight: 400 }}
                          >
                            {versionModelConfig.model}
                          </Box>
                        </Typography>
                        {versionModelConfig.temperature !== undefined && (
                          <Typography
                            variant="body2"
                            display="block"
                            sx={{
                              mb: 1,
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: textSecondary,
                                fontWeight: 500,
                                mr: 1.5,
                                minWidth: 90,
                                display: 'inline-block',
                              }}
                            >
                              Temperature:
                            </Box>
                            <Box
                              component="span"
                              sx={{ color: warningColor, fontWeight: 500 }}
                            >
                              {versionModelConfig.temperature}
                            </Box>
                          </Typography>
                        )}
                        {versionModelConfig.max_tokens !== undefined && (
                          <Typography
                            variant="body2"
                            display="block"
                            sx={{
                              mb: 1,
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                color: textSecondary,
                                fontWeight: 500,
                                mr: 1.5,
                                minWidth: 90,
                                display: 'inline-block',
                              }}
                            >
                              Max Tokens:
                            </Box>
                            <Box
                              component="span"
                              sx={{ color: warningColor, fontWeight: 500 }}
                            >
                              {versionModelConfig.max_tokens}
                            </Box>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                };

                return (
                  <Tooltip
                    key={version.version}
                    title={createVersionTooltipContent()}
                    arrow
                    placement="right"
                    enterDelay={300}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'transparent',
                          boxShadow: 'none',
                          padding: 0,
                          maxWidth: 'none',
                        },
                      },
                      arrow: {
                        sx: {
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.grey[900]
                              : theme.palette.background.paper,
                        },
                      },
                    }}
                  >
                    <Chip
                      label={`v${version.version}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderColor:
                          version.version === selectedTemplate.version
                            ? theme.palette.primary.main
                            : theme.palette.divider,
                        backgroundColor:
                          version.version === selectedTemplate.version
                            ? theme.palette.primary.light ||
                              adjustOpacity(theme.palette.primary.main, 0.1)
                            : 'transparent',
                        color:
                          version.version === selectedTemplate.version
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        '& .MuiChip-label': {
                          px: 1,
                        },
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          borderColor: 'primary.main',
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8125rem',
              color: theme.palette.text.secondary,
            }}
          >
            No additional versions available
          </Typography>
        )}

        {longLoading && templateLoading && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              fontSize: '0.8125rem',
              backgroundColor:
                theme.palette.warning.light || theme.palette.background.default,
              color: theme.palette.text.secondary,
              border: `1px solid ${theme.palette.divider}`,
              '& .MuiAlert-icon': {
                color: theme.palette.warning.main,
              },
            }}
          >
            Loading is taking longer than expected. You may continue filling
            fields or try again shortly.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default TemplateInfoCard;
